import { PurchaseType } from '@/enums/purchase-type';
import type {
  OffDeviceRefundLegType,
  OffDeviceRefundMethod,
  PosProviderAdapterKey,
  PosRefund,
  PosTransaction
} from '@/data/posService';

/**
 * Provider adapters whose paid sale can only be refunded outside the device
 * (OFF_DEVICE_REFUND_ADAPTER_KEYS in uyanik-backend pos-fiscal.constants.ts).
 * Pavo is not here: it has a real refund API.
 */
export const OFF_DEVICE_REFUND_ADAPTER_KEYS: readonly PosProviderAdapterKey[] =
  ['TOKEN', 'INPOS'];

/** One tender of a sale, with the refund methods the server accepts for it. */
export interface OffDeviceRefundLegOption {
  type: OffDeviceRefundLegType;
  amount: number;
  methods: OffDeviceRefundMethod[];
  defaultMethod: OffDeviceRefundMethod;
}

// Mirrors OFF_DEVICE_REFUND_ALLOWED_METHODS in uyanik-backend
// pos-fiscal.constants.ts: card and meal money may also go back in cash; an
// Açık Hesap leg collected nothing, it can only be voided.
const ALLOWED_METHODS: Record<OffDeviceRefundLegType, OffDeviceRefundMethod[]> =
  {
    cash: ['CASH'],
    card: ['BANK_APP', 'CASH'],
    meal: ['MEAL_PROVIDER', 'CASH'],
    open_account: ['OPEN_ACCOUNT_VOID']
  };

const LEG_TYPES: OffDeviceRefundLegType[] = [
  'cash',
  'card',
  'meal',
  'open_account'
];

const legTypeForPurchaseType = (
  purchaseType?: PurchaseType | string | null
): OffDeviceRefundLegType | null => {
  switch (purchaseType) {
    case PurchaseType.CASH:
      return 'cash';
    case PurchaseType.PHYSICAL_CARD:
      return 'card';
    case PurchaseType.MEAL_CARD:
      return 'meal';
    case PurchaseType.OPEN_ACCOUNT:
      return 'open_account';
    default:
      return null;
  }
};

/** Token / InPOS sale: refunded outside the device, recorded with its own form. */
export const isOffDeviceRefundTransaction = (tx: PosTransaction): boolean => {
  const adapterKey = tx.provider?.adapterKey;
  return !!adapterKey && OFF_DEVICE_REFUND_ADAPTER_KEYS.includes(adapterKey);
};

/**
 * The tenders of the sale the way the server derives them (Token: observed
 * breakdown, else purchaseType; InPOS: purchaseType). `null` when the tender
 * cannot be read (the server refuses that sale too). InPOS never voids an Açık
 * Hesap, and neither does a Cari Tahsilat refund.
 */
export const deriveOffDeviceRefundLegs = (
  tx: PosTransaction
): OffDeviceRefundLegOption[] | null => {
  const isToken = tx.provider?.adapterKey === 'TOKEN';
  const breakdown = Array.isArray(tx.paymentBreakdown)
    ? tx.paymentBreakdown
    : [];
  let legs: { type: OffDeviceRefundLegType; amount: number }[];
  if (isToken && breakdown.length > 0) {
    if (
      breakdown.some(
        (leg) => !LEG_TYPES.includes(leg.type as OffDeviceRefundLegType)
      )
    )
      return null;
    legs = breakdown.map((leg) => ({
      type: leg.type as OffDeviceRefundLegType,
      amount: Number(leg.amount)
    }));
  } else {
    const type = legTypeForPurchaseType(tx.purchaseType);
    if (!type) return null;
    legs = [{ type, amount: Number(tx.amount) }];
  }
  const noOpenAccount = !isToken || tx.targetType === 'RECEIVABLE_COLLECTION';
  if (noOpenAccount && legs.some((leg) => leg.type === 'open_account'))
    return null;
  return legs.map((leg) => ({
    ...leg,
    methods: ALLOWED_METHODS[leg.type],
    defaultMethod: ALLOWED_METHODS[leg.type][0]
  }));
};

export type RefundOutcome =
  | { kind: 'success' }
  | { kind: 'pending' }
  | { kind: 'failed'; message?: string };

/**
 * What a refund answer really says. The refund endpoint answers 201 even when
 * the device rejected it (status FAILED), so a success message must never be
 * shown off the HTTP status alone.
 */
export const readRefundOutcome = (
  refund: Pick<PosRefund, 'status' | 'providerErrorMessage'>
): RefundOutcome => {
  if (refund.status === 'APPROVED') return { kind: 'success' };
  if (refund.status === 'PENDING') return { kind: 'pending' };
  return {
    kind: 'failed',
    message: refund.providerErrorMessage ?? undefined
  };
};

/**
 * The sale's own record after a refund: MANUAL_REVIEW means the money went
 * back but the order / check / ticket could not be undone automatically.
 */
export const needsManualReview = (tx?: {
  recoveryStatus?: string | null;
}): boolean => tx?.recoveryStatus === 'MANUAL_REVIEW';
